/**
 * RatingInput Component
 *
 * Interactive star rating input (1-5) with:
 * - Hover preview showing potential selection
 * - Click to select rating
 * - onChange callback with rating value
 * - Configurable size (sm, md, lg)
 * - SVG star icons
 */

import { useState, useCallback } from 'react';

interface RatingInputProps {
  /** Current rating value (1-5, or null if unset) */
  value: number | null;
  /** Callback when rating changes */
  onChange: (rating: number) => void;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the input is disabled */
  disabled?: boolean;
}

const SIZE_CONFIG = {
  sm: { icon: 'w-5 h-5', gap: 'gap-0.5' },
  md: { icon: 'w-6 h-6', gap: 'gap-1' },
  lg: { icon: 'w-8 h-8', gap: 'gap-1.5' },
};

function StarIcon({ filled, className }: { filled: boolean; className: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
      />
    </svg>
  );
}

export function RatingInput({
  value,
  onChange,
  size = 'md',
  disabled = false,
}: RatingInputProps) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const config = SIZE_CONFIG[size];

  const handleMouseEnter = useCallback(
    (star: number) => {
      if (!disabled) {
        setHoverValue(star);
      }
    },
    [disabled]
  );

  const handleMouseLeave = useCallback(() => {
    setHoverValue(null);
  }, []);

  const handleClick = useCallback(
    (star: number) => {
      if (!disabled) {
        onChange(star);
      }
    },
    [disabled, onChange]
  );

  const displayValue = hoverValue ?? value ?? 0;

  return (
    <div
      className={`flex items-center ${config.gap}`}
      onMouseLeave={handleMouseLeave}
      role="radiogroup"
      aria-label="Rating"
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const isFilled = star <= displayValue;
        const isHovering = hoverValue !== null && star <= hoverValue;

        return (
          <button
            key={star}
            type="button"
            onClick={() => handleClick(star)}
            onMouseEnter={() => handleMouseEnter(star)}
            disabled={disabled}
            className={`
              transition-all duration-150 ease-out
              ${isFilled ? 'text-amber-400' : 'text-gray-300'}
              ${!disabled && 'hover:scale-110 cursor-pointer'}
              ${disabled && 'cursor-not-allowed opacity-50'}
              ${isHovering && !disabled ? 'text-amber-400' : ''}
              focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-1 rounded
            `}
            role="radio"
            aria-checked={value === star}
            aria-label={`${star} star${star > 1 ? 's' : ''}`}
            data-testid={`star-${star}`}
          >
            <StarIcon filled={isFilled} className={config.icon} />
          </button>
        );
      })}
    </div>
  );
}
